import React, { useState } from 'react';
import axios, { AxiosError } from 'axios';
import { Button } from 'reactstrap';
import { FaPlus } from 'react-icons/fa';
import { useBoolean } from 'usehooks-ts';
import Swal from 'sweetalert2';

import { TypeMaster } from 'models/type/TypeMaster';
import { extractAccessToken } from 'helpers/decodeLocalStorage';
import useLoader from 'hooks/useLoader';

import {
    WorkflowTypeMasterFormModal,
    WorkflowTypeMasterTable
} from './components';
import {
    useAddWorkflowTypeMaster,
    useDeleteWorkflowTypeMaster,
    useEditWorkflowTypeMaster,
    useWorkflowTypeMaster
} from './queries';

function getErrorMessage(error: unknown) {
    return axios.isAxiosError(error)
        ? (error as AxiosError<{ message?: string }>).response?.data.message
        : error instanceof Error
        ? error.message
        : 'Unknown error occurred.';
}

export const WorkflowTypeMasterConfig = () => {
    const loader = useLoader();

    const {
        data: workflowTypeConfigs,
        isFetching: isFetchingWorkflowTypeConfigs,
        refetch: refetchWorkflowTypeConfigs
    } = useWorkflowTypeMaster();

    const { value: isOpenCreateModal, toggle: toggleIsOpenCreateModal } =
        useBoolean();
    const { mutate: addWorkflowTypeMaster } = useAddWorkflowTypeMaster();

    const [selectedEditWorkflowTypeMaster, setSelectedEditWorkflowTypeMaster] =
        useState<TypeMaster>();
    const isOpenEditModal = !!selectedEditWorkflowTypeMaster;
    const { mutate: editWorkflowTypeMaster } = useEditWorkflowTypeMaster();

    const { mutate: deleteWorkflowTypeMaster } = useDeleteWorkflowTypeMaster();

    const handleSubmitAddWorkflowType = (formValues: TypeMaster) => {
        const request = {
            ...formValues,
            sid: '*',
            created_by:
                extractAccessToken().citizen_id || extractAccessToken().username
        };

        loader.show();

        addWorkflowTypeMaster(request, {
            onSuccess: async () => {
                await Swal.fire({
                    title: 'Success!',
                    icon: 'success'
                });
            },
            onError: async error => {
                await Swal.fire({
                    title: 'Error!',
                    icon: 'error',
                    text: getErrorMessage(error)
                });
            },
            onSettled: () => {
                loader.hide();
                toggleIsOpenCreateModal();

                const _ = refetchWorkflowTypeConfigs();
            }
        });
    };

    const handleEditWorkflowTypeMaster = (typeMaster: TypeMaster) => {
        setSelectedEditWorkflowTypeMaster(typeMaster);
    };

    const handleCloseEditModal = () => {
        setSelectedEditWorkflowTypeMaster(undefined);
    };

    const handleSubmitEditWorkflowType = (formValues: TypeMaster) => {
        const request = {
            ...formValues,
            updated_by:
                extractAccessToken().citizen_id || extractAccessToken().username
        };

        loader.show();

        editWorkflowTypeMaster(request, {
            onSuccess: async () => {
                await Swal.fire({
                    title: 'Success!',
                    icon: 'success'
                });
            },
            onError: async error => {
                await Swal.fire({
                    title: 'Error!',
                    icon: 'error',
                    text: getErrorMessage(error)
                });
            },
            onSettled: () => {
                loader.hide();
                handleCloseEditModal();

                const _ = refetchWorkflowTypeConfigs();
            }
        });
    };

    const handleDeleteWorkflowTypeMaster = async (typeMaster: TypeMaster) => {
        const result = await Swal.fire({
            title: `ยืนยันการลบ ${typeMaster.workflowtypeid}?`,
            icon: 'warning',
            showDenyButton: true,
            denyButtonText: 'ยกเลิก',
            confirmButtonText: 'ยืนยัน'
        });
        if (!result.isConfirmed) {
            return;
        }

        loader.show();

        deleteWorkflowTypeMaster(typeMaster, {
            onSuccess: async () => {
                await Swal.fire({
                    title: 'Success!',
                    icon: 'success'
                });
            },
            onError: async error => {
                await Swal.fire({
                    title: 'Error!',
                    icon: 'error',
                    text: getErrorMessage(error)
                });
            },
            onSettled: () => {
                loader.hide();

                const _ = refetchWorkflowTypeConfigs();
            }
        });
    };

    return (
        <div className="master-data-content">
            <div className="flex justify-between">
                <div
                    className="trapezoid-head"
                    style={{ width: '200px' }}
                >
                    <span>Workflow Type Master Config</span>
                </div>

                <Button
                    size="sm"
                    color="primary"
                    onClick={toggleIsOpenCreateModal}
                    className="flex items-center gap-1 self-center"
                >
                    <FaPlus />
                    <span>Create</span>
                </Button>
            </div>
            <div className="mat-box">
                <WorkflowTypeMasterTable
                    isPending={isFetchingWorkflowTypeConfigs}
                    workflowTypeConfigs={workflowTypeConfigs}
                    onEdit={handleEditWorkflowTypeMaster}
                    onDelete={handleDeleteWorkflowTypeMaster}
                />

                {/* Create modal */}
                <WorkflowTypeMasterFormModal
                    title="Create Workflow Type"
                    isOpen={isOpenCreateModal}
                    onClose={toggleIsOpenCreateModal}
                    onSubmit={handleSubmitAddWorkflowType}
                />

                {/* Edit modal */}
                <WorkflowTypeMasterFormModal
                    disabledKeyField
                    title="Edit Workflow Type"
                    defaultValues={selectedEditWorkflowTypeMaster}
                    isOpen={isOpenEditModal}
                    onClose={handleCloseEditModal}
                    onSubmit={handleSubmitEditWorkflowType}
                />
            </div>
        </div>
    );
};
